import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Loader2, Clock } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const { exchangeGoogle } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    if (!sessionId) {
      navigate("/login", { replace: true });
      return;
    }

    exchangeGoogle(sessionId)
      .then((result) => {
        // Clear the hash so a refresh doesn't re-process it.
        window.history.replaceState(null, "", window.location.pathname);
        if (result?.pending) {
          setPending(result.message || "Your request to join is pending approval. We'll email you once you're approved.");
          return;
        }
        navigate("/portal", { replace: true });
      })
      .catch(() => {
        setError("We couldn't sign you in with Google. Please try again.");
        setTimeout(() => navigate("/login", { replace: true }), 2200);
      });
  }, [exchangeGoogle, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="text-center">
        {error ? (
          <p className="text-destructive" data-testid="auth-callback-error">{error}</p>
        ) : pending ? (
          <div className="mx-auto flex max-w-md flex-col items-center gap-4" data-testid="auth-callback-pending">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
              <Clock className="h-7 w-7 text-primary" />
            </div>
            <h1 className="font-serif text-2xl font-semibold text-foreground">Request received</h1>
            <p className="text-muted-foreground">{pending}</p>
            <div className="mt-2 flex w-full max-w-xs flex-col gap-2">
              <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="auth-callback-home-button">
                <Link to="/">Back to home</Link>
              </Button>
              <Button asChild variant="ghost" className="w-full" data-testid="auth-callback-signin-button">
                <Link to="/login">Go to sign in</Link>
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 text-muted-foreground">
            <Loader2 className="h-7 w-7 animate-spin text-primary" />
            <p>Signing you in…</p>
          </div>
        )}
      </div>
    </div>
  );
}
